import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import api from '../services/api';
import useUnreadMessages from '../hooks/useUnreadMessages';
import { Card, Input, PillButton, Chip, Toggle, SegmentedControl, EmptyState, Modal, Toast, IconCircle } from '../ui';
import { ReactComponent as MessageCircleIcon } from '../assets/icons/message-circle.svg';
import { ReactComponent as PhoneIcon } from '../assets/icons/phone.svg';
import { ReactComponent as TelegramIcon } from '../assets/icons/telegram.svg';
import { ReactComponent as DiscordIcon } from '../assets/icons/discord.svg';
import { ReactComponent as SlackIcon } from '../assets/icons/slack-brand.svg';
import { ReactComponent as WhatsappIcon } from '../assets/icons/whatsapp.svg';
import { ReactComponent as MailIcon } from '../assets/icons/mail.svg';
import { ReactComponent as AndroidIcon } from '../assets/icons/android-brand.svg';
import { ReactComponent as SearchIcon } from '../assets/icons/search.svg';
import { ReactComponent as CopyIcon } from '../assets/icons/copy.svg';
import { ReactComponent as SendIcon } from '../assets/icons/send.svg';
import { ReactComponent as TrashIcon } from '../assets/icons/trash.svg';
import { ReactComponent as PlusIcon } from '../assets/icons/plus.svg';
import { ReactComponent as ArrowLeftIcon } from '../assets/icons/arrow-left.svg';
import { ReactComponent as RefreshIcon } from '../assets/icons/refresh-cw.svg';
import { ReactComponent as KeyRoundIcon } from '../assets/icons/key-round.svg';
import { ReactComponent as ShieldCheckIcon } from '../assets/icons/shield-check.svg';
import { ReactComponent as CircleCheckIcon } from '../assets/icons/circle-check.svg';
import { withWsToken } from '../services/wsAuth';
import './Messages.css';

// Mismo patrón de conexión que components/RealtimeNotifications.js: mismo
// origen que el resto de la app, producción proxea /ws desde el vhost.
const WS_URL = process.env.REACT_APP_WS_URL ||
  (window.location.protocol === 'https:' ? 'wss://' : 'ws://') +
  window.location.host + '/ws';

// Metadatos por tipo de canal: icono para IconCircle y clave de traducción.
// android_sms y android_notification comparten icono con sms/whatsapp porque
// para el usuario final es "un SMS" o "una app de mensajería", no un canal
// distinto — solo cambia por dónde entró.
const CHANNEL_META = {
  sms: { icon: MessageCircleIcon, labelKey: 'messagesPage.channelType.sms' },
  call: { icon: PhoneIcon, labelKey: 'messagesPage.channelType.call' },
  telegram: { icon: TelegramIcon, labelKey: 'messagesPage.channelType.telegram' },
  discord: { icon: DiscordIcon, labelKey: 'messagesPage.channelType.discord' },
  slack: { icon: SlackIcon, labelKey: 'messagesPage.channelType.slack' },
  email: { icon: MailIcon, labelKey: 'messagesPage.channelType.email' },
  android_sms: { icon: MessageCircleIcon, labelKey: 'messagesPage.channelType.sms' },
  android_notification: { icon: WhatsappIcon, labelKey: 'messagesPage.channelType.androidNotification' },
};

const CHANNEL_FILTERS = ['all', 'sms', 'call', 'telegram', 'discord', 'slack', 'email', 'android_notification'];

// Tipos de canal que se pueden crear a mano desde el panel. sms/call llegan
// por Twilio (número compartido, no hace falta crear canal) y android_* solo
// los crea la app al emparejarse con la clave de ingesta.
const CREATABLE_CHANNEL_TYPES = ['telegram', 'discord', 'slack', 'email', 'sms', 'call'];

function relativeTime(iso, t) {
  if (!iso) return '';
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (minutes < 1) return t('messagesPage.justNow');
  if (minutes < 60) return t('messagesPage.minutesAgo', { count: minutes });
  const hours = Math.round(minutes / 60);
  if (hours < 24) return t('messagesPage.hoursAgo', { count: hours });
  return t('messagesPage.daysAgo', { count: Math.round(hours / 24) });
}

function channelMeta(type) {
  return CHANNEL_META[type] || CHANNEL_META.sms;
}

/* ------------------------------------------------------------------ */
/* Bandeja de entrada                                                  */
/* ------------------------------------------------------------------ */

function InboxTab() {
  const { t } = useTranslation();
  const { refresh: refreshUnread } = useUnreadMessages(0); // sin sondeo propio: el badge de la nav ya sondea

  const [filterChannel, setFilterChannel] = useState('all');
  const [onlyOtp, setOnlyOtp] = useState(false);
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [forwardTarget, setForwardTarget] = useState(null); // id del mensaje a reenviar
  const [toast, setToast] = useState(null);

  // Debounce del buscador: es búsqueda en servidor (`q`), no filtrado local.
  const searchTimer = useRef(null);
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(searchTimer.current);
  }, [searchInput]);

  const load = useCallback((cursor) => {
    if (cursor) setLoadingMore(true); else setLoading(true);
    const params = { limit: 50 };
    if (filterChannel !== 'all') params.channel = filterChannel;
    if (onlyOtp) params.otp = 1;
    if (onlyUnread) params.unread = 1;
    if (search) params.q = search;
    if (cursor) params.cursor = cursor;
    api.get('/messages', { params })
      .then((res) => {
        setMessages((prev) => (cursor ? [...prev, ...(res.data?.messages || [])] : (res.data?.messages || [])));
        setNextCursor(res.data?.nextCursor || null);
      })
      .catch(() => { if (!cursor) setMessages([]); })
      .finally(() => { setLoading(false); setLoadingMore(false); });
  }, [filterChannel, onlyOtp, onlyUnread, search]);

  useEffect(() => { load(); }, [load]);

  // Tiempo real: mismo evento que espera el backend, `message_received`. El
  // contrato dice que trae `{message}`; por si el payload llega envuelto en
  // `data` (como sms_received/call_received en RealtimeNotifications.js) se
  // acepta cualquiera de las dos formas.
  useEffect(() => {
    let ws;
    try { ws = new WebSocket(withWsToken(WS_URL)); } catch { return undefined; }
    ws.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        if (payload.event !== 'message_received') return;
        const msg = payload.message || payload.data?.message || payload.data;
        if (!msg?.id) return;
        setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [msg, ...prev]));
        refreshUnread();
      } catch { /* payload no JSON: se ignora */ }
    };
    return () => ws.close();
  }, [refreshUnread]);

  const selectMessage = useCallback((id) => {
    setSelectedId(id);
    setSelected(null);
    api.get(`/messages/${id}`)
      .then((res) => {
        setSelected(res.data);
        if (!res.data?.readAt) {
          api.post(`/messages/${id}/read`)
            .then(() => {
              setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, readAt: new Date().toISOString() } : m)));
              refreshUnread();
            })
            .catch(() => {});
        }
      })
      .catch(() => setSelected(null));
  }, [refreshUnread]);

  const remove = useCallback(async (id) => {
    if (!window.confirm(t('messagesPage.confirmDelete'))) return;
    try {
      await api.delete(`/messages/${id}`);
      setMessages((prev) => prev.filter((m) => m.id !== id));
      if (selectedId === id) { setSelectedId(null); setSelected(null); }
    } catch {
      setToast({ type: 'error', message: t('messagesPage.deleteError') });
    }
  }, [selectedId, t]);

  const copyOtp = useCallback((code) => {
    navigator.clipboard.writeText(code);
    setToast({ type: 'success', message: t('messagesPage.otpCopied') });
  }, [t]);

  return (
    <div className={`sgc-messages__columns ${selectedId ? 'sgc-messages__columns--detail-open' : ''}`}>
      <div className="sgc-messages__list-col">
        <Input icon={SearchIcon} placeholder={t('common.search')} value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />

        <div className="sgc-messages__chip-row">
          {CHANNEL_FILTERS.map((f) => (
            <Chip
              key={f}
              label={t(`messagesPage.filters.${f}`)}
              selected={filterChannel === f}
              onClick={() => setFilterChannel(f)}
            />
          ))}
        </div>
        <div className="sgc-messages__chip-row">
          <Chip icon={ShieldCheckIcon} label={t('messagesPage.filters.otpOnly')} selected={onlyOtp} onClick={() => setOnlyOtp((v) => !v)} />
          <Chip icon={CircleCheckIcon} label={t('messagesPage.filters.unread')} selected={onlyUnread} onClick={() => setOnlyUnread((v) => !v)} />
        </div>

        {loading ? (
          <p className="sgc-messages__loading">{t('common.loading')}</p>
        ) : messages.length === 0 ? (
          <EmptyState icon={MessageCircleIcon} title={t('messagesPage.emptyTitle')} description={t('messagesPage.emptyDescription')} />
        ) : (
          <>
            <ul className="sgc-messages__list">
              {messages.map((m) => {
                const meta = channelMeta(m.channelType);
                return (
                  <li key={m.id}>
                    <button
                      type="button"
                      className={`sgc-messages__row ${selectedId === m.id ? 'sgc-messages__row--active' : ''}`}
                      onClick={() => selectMessage(m.id)}
                    >
                      <IconCircle icon={meta.icon} tone={m.otpCode ? 'accent' : 'surface'} size={40} />
                      <div className="sgc-messages__row-body">
                        <div className="sgc-messages__row-top">
                          <span className="sgc-messages__row-sender">{m.sender || m.subject || t('messagesPage.unknownSender')}</span>
                          <span className="sgc-messages__row-time">{relativeTime(m.receivedAt, t)}</span>
                        </div>
                        <p className="sgc-messages__row-preview">{m.preview}</p>
                      </div>
                      {!m.readAt && <span className="sgc-messages__row-dot" aria-hidden="true" />}
                      {m.otpCode && (
                        <span
                          className="sgc-messages__otp-pill"
                          onClick={(e) => { e.stopPropagation(); copyOtp(m.otpCode); }}
                        >
                          {m.otpCode}
                          <CopyIcon width={12} height={12} aria-hidden="true" />
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
            {nextCursor && (
              <PillButton variant="secondary" size="sm" loading={loadingMore} onClick={() => load(nextCursor)}>
                {t('messagesPage.loadMore')}
              </PillButton>
            )}
          </>
        )}
      </div>

      <div className="sgc-messages__detail-col">
        {!selectedId ? (
          <EmptyState icon={MessageCircleIcon} title={t('messagesPage.selectHint')} className="sgc-messages__detail-empty" />
        ) : !selected ? (
          <p className="sgc-messages__loading">{t('common.loading')}</p>
        ) : (
          <>
            <button type="button" className="sgc-messages__detail-back" onClick={() => setSelectedId(null)}>
              <ArrowLeftIcon width={16} height={16} aria-hidden="true" />
              {t('common.back')}
            </button>
            <div className="sgc-messages__detail-header">
              <IconCircle icon={channelMeta(selected.channelType).icon} size={44} />
              <div>
                <p className="sgc-messages__detail-sender">{selected.sender || t('messagesPage.unknownSender')}</p>
                <p className="sgc-messages__detail-meta">
                  {selected.recipient ? `${selected.recipient} · ` : ''}{new Date(selected.receivedAt).toLocaleString()}
                </p>
              </div>
            </div>
            {selected.subject && <p className="sgc-messages__detail-subject">{selected.subject}</p>}
            {selected.otpCode && (
              <button type="button" className="sgc-messages__otp-pill sgc-messages__otp-pill--big" onClick={() => copyOtp(selected.otpCode)}>
                {selected.otpCode}
                <CopyIcon width={14} height={14} aria-hidden="true" />
              </button>
            )}
            <p className="sgc-messages__detail-body">{selected.body}</p>
            <div className="sgc-messages__detail-actions">
              <PillButton variant="secondary" size="sm" icon={SendIcon} onClick={() => setForwardTarget(selected.id)}>
                {t('messagesPage.forward')}
              </PillButton>
              <PillButton variant="danger" size="sm" icon={TrashIcon} onClick={() => remove(selected.id)}>
                {t('common.delete')}
              </PillButton>
            </div>
          </>
        )}
      </div>

      <ForwardSheet
        open={!!forwardTarget}
        messageId={forwardTarget}
        onClose={() => setForwardTarget(null)}
        onSent={() => setToast({ type: 'success', message: t('messagesPage.forwardSent') })}
      />

      {toast && <div className="sgc-messages__toast"><Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} /></div>}
    </div>
  );
}

const FORWARD_DESTINATIONS = ['push', 'email', 'telegram', 'webhook'];

function ForwardSheet({ open, messageId, onClose, onSent }) {
  const { t } = useTranslation();
  const [type, setType] = useState('push');
  const [target, setTarget] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (open) { setType('push'); setTarget(''); setError(''); } }, [open]);

  const send = useCallback(async () => {
    if (!messageId) return;
    setSending(true);
    setError('');
    try {
      await api.post(`/messages/${messageId}/forward`, { destination: { type, target: target || undefined } });
      onSent?.();
      onClose?.();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSending(false);
    }
  }, [messageId, type, target, onSent, onClose]);

  return (
    <Modal open={open} onClose={onClose} title={t('messagesPage.forward')}>
      <div className="sgc-messages__forward-sheet">
        <SegmentedControl
          options={FORWARD_DESTINATIONS.map((d) => ({ value: d, label: t(`messagesPage.destination.${d}`) }))}
          value={type}
          onChange={setType}
        />
        {type !== 'push' && (
          <Input
            label={t(`messagesPage.destinationTarget.${type}`)}
            value={target}
            onChange={(e) => setTarget(e.target.value)}
          />
        )}
        {error && <p className="sgc-input__error">{error}</p>}
        <PillButton loading={sending} onClick={send}>{t('messagesPage.forward')}</PillButton>
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------------ */
/* Canales                                                             */
/* ------------------------------------------------------------------ */

function ChannelForm({ initialType, onCreated, onClose }) {
  const { t } = useTranslation();
  const [type, setType] = useState(initialType || 'telegram');
  const [name, setName] = useState('');
  const [botToken, setBotToken] = useState('');
  const [channelIds, setChannelIds] = useState('');
  const [signingSecret, setSigningSecret] = useState('');
  const [host, setHost] = useState('');
  const [port, setPort] = useState('993');
  const [secure, setSecure] = useState(true);
  const [user, setUser] = useState('');
  const [password, setPassword] = useState('');
  const [folder, setFolder] = useState('INBOX');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [callbackConfig, setCallbackConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null); // { slackWebhookUrl } tras crear un canal de Slack

  useEffect(() => {
    if (type === 'sms' || type === 'call') {
      api.get('/callbacks/config').then((res) => setCallbackConfig(res.data)).catch(() => setCallbackConfig(null));
    }
  }, [type]);

  const configFor = () => {
    switch (type) {
      case 'telegram': return { botToken };
      case 'discord': return { botToken, channelIds: channelIds.split(',').map((s) => s.trim()).filter(Boolean) };
      case 'slack': return { botToken, signingSecret };
      case 'email': return { host, port: Number(port), secure, user, password, folder };
      case 'sms':
      case 'call': return { phoneNumber };
      default: return {};
    }
  };

  const save = useCallback(async () => {
    setSaving(true);
    setError('');
    try {
      const res = await api.post('/messages/channels', { type, name, config: configFor() });
      if (type === 'slack' && res.data?.id) {
        setResult({ slackWebhookUrl: `${window.location.origin}/api/messages/hooks/slack/${res.data.id}` });
      } else {
        onCreated?.();
        onClose?.();
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, name, botToken, channelIds, signingSecret, host, port, secure, user, password, folder, phoneNumber]);

  if (result) {
    return (
      <div className="sgc-messages__channel-form">
        <p>{t('messagesPage.slackWebhookHint')}</p>
        <Input readOnly value={result.slackWebhookUrl} rightAction={(
          <button type="button" onClick={() => navigator.clipboard.writeText(result.slackWebhookUrl)}>
            <CopyIcon width={16} height={16} aria-hidden="true" />
          </button>
        )} />
        <PillButton onClick={() => { onCreated?.(); onClose?.(); }}>{t('common.close')}</PillButton>
      </div>
    );
  }

  return (
    <div className="sgc-messages__channel-form">
      {!initialType && (
        <SegmentedControl
          options={CREATABLE_CHANNEL_TYPES.map((ty) => ({ value: ty, label: t(`messagesPage.channelType.${ty}`) }))}
          value={type}
          onChange={setType}
        />
      )}
      <Input label={t('common.name')} value={name} onChange={(e) => setName(e.target.value)} />

      {type === 'telegram' && (
        <>
          <Input label={t('messagesPage.botToken')} value={botToken} onChange={(e) => setBotToken(e.target.value)} />
          <p className="sgc-messages__help">{t('messagesPage.help.telegram')}</p>
        </>
      )}
      {type === 'discord' && (
        <>
          <Input label={t('messagesPage.botToken')} value={botToken} onChange={(e) => setBotToken(e.target.value)} />
          <Input label={t('messagesPage.channelIds')} value={channelIds} onChange={(e) => setChannelIds(e.target.value)} placeholder="123456, 789012" />
          <p className="sgc-messages__help">{t('messagesPage.help.discord')}</p>
        </>
      )}
      {type === 'slack' && (
        <>
          <Input label={t('messagesPage.botToken')} value={botToken} onChange={(e) => setBotToken(e.target.value)} />
          <Input label={t('messagesPage.signingSecret')} value={signingSecret} onChange={(e) => setSigningSecret(e.target.value)} />
          <p className="sgc-messages__help">{t('messagesPage.help.slack')}</p>
        </>
      )}
      {type === 'email' && (
        <>
          <Input label={t('messagesPage.imapHost')} value={host} onChange={(e) => setHost(e.target.value)} />
          <Input label={t('common.port')} value={port} onChange={(e) => setPort(e.target.value)} />
          <Toggle label="TLS" checked={secure} onChange={setSecure} />
          <Input label={t('common.name')} placeholder={t('messagesPage.emailPlaceholder')} value={user} onChange={(e) => setUser(e.target.value)} />
          <Input label={t('common.password')} type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
          <Input label={t('messagesPage.folder')} value={folder} onChange={(e) => setFolder(e.target.value)} />
          <p className="sgc-messages__help">{t('messagesPage.help.email')}</p>
        </>
      )}
      {(type === 'sms' || type === 'call') && (
        <>
          <Input label={t('messagesPage.phoneNumber')} value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} />
          {callbackConfig && (
            <div className="sgc-messages__webhook-urls">
              <p className="sgc-messages__help">{t('messagesPage.webhookUrls')}</p>
              {[
                { label: 'SMS', value: callbackConfig.smsWebhook },
                { label: 'CALL', value: callbackConfig.callWebhook },
              ].map((row) => (
                <Input key={row.label} label={row.label} readOnly value={row.value || ''} rightAction={(
                  <button type="button" onClick={() => navigator.clipboard.writeText(row.value || '')} disabled={!row.value}>
                    <CopyIcon width={16} height={16} aria-hidden="true" />
                  </button>
                )} />
              ))}
            </div>
          )}
        </>
      )}

      {error && <p className="sgc-input__error">{error}</p>}
      <PillButton loading={saving} onClick={save}>{t('common.save')}</PillButton>
    </div>
  );
}

function ChannelsTab() {
  const { t } = useTranslation();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [testing, setTesting] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/messages/channels').then((res) => setChannels(res.data?.channels || res.data || [])).catch(() => setChannels([])).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const test = useCallback(async (id) => {
    setTesting(id);
    try {
      await api.post(`/messages/channels/${id}/test`);
      setToast({ type: 'success', message: t('messagesPage.testOk') });
      load();
    } catch (e) {
      setToast({ type: 'error', message: e.response?.data?.error || t('messagesPage.testError') });
    } finally {
      setTesting(null);
    }
  }, [t, load]);

  const remove = useCallback(async (id) => {
    if (!window.confirm(t('messagesPage.confirmDeleteChannel'))) return;
    try {
      await api.delete(`/messages/channels/${id}`);
      setChannels((prev) => prev.filter((c) => c.id !== id));
    } catch {
      setToast({ type: 'error', message: t('messagesPage.deleteError') });
    }
  }, [t]);

  return (
    <div className="sgc-messages__section">
      <div className="sgc-messages__section-header">
        <h2>{t('messagesPage.tabs.channels')}</h2>
        <PillButton icon={PlusIcon} size="sm" onClick={() => setShowAdd(true)}>{t('messagesPage.addChannel')}</PillButton>
      </div>

      {loading ? (
        <p className="sgc-messages__loading">{t('common.loading')}</p>
      ) : channels.length === 0 ? (
        <EmptyState icon={AndroidIcon} title={t('messagesPage.emptyChannels')} actionLabel={t('messagesPage.addChannel')} onAction={() => setShowAdd(true)} />
      ) : (
        <div className="sgc-messages__channel-grid">
          {channels.map((c) => (
            <Card key={c.id} className="sgc-messages__channel-card">
              <div className="sgc-messages__channel-card-top">
                <IconCircle icon={channelMeta(c.type).icon} />
                <div>
                  <p className="sgc-messages__channel-name">{c.name}</p>
                  <p className={`sgc-messages__channel-status sgc-messages__channel-status--${c.status === 'error' ? 'error' : 'ok'}`}>
                    {c.status === 'error' ? (c.lastError || t('messagesPage.statusError')) : t('messagesPage.statusActive')}
                  </p>
                </div>
              </div>
              {c.lastSyncAt && <p className="sgc-messages__help">{t('messagesPage.lastSync')}: {new Date(c.lastSyncAt).toLocaleString()}</p>}
              <div className="sgc-messages__channel-card-actions">
                <button type="button" onClick={() => test(c.id)} disabled={testing === c.id} title={t('messagesPage.test')}>
                  <RefreshIcon width={16} height={16} aria-hidden="true" />
                </button>
                <button type="button" onClick={() => remove(c.id)} title={t('common.delete')}>
                  <TrashIcon width={16} height={16} aria-hidden="true" />
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal open={showAdd} onClose={() => setShowAdd(false)} title={t('messagesPage.addChannel')}>
        <ChannelForm onCreated={load} onClose={() => setShowAdd(false)} />
      </Modal>

      {toast && <div className="sgc-messages__toast"><Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} /></div>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Reglas de reenvío                                                   */
/* ------------------------------------------------------------------ */

function RulesTab() {
  const { t } = useTranslation();
  const [rules, setRules] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [toast, setToast] = useState(null);

  // Formulario de creación
  const [channelId, setChannelId] = useState('');
  const [sender, setSender] = useState('');
  const [contains, setContains] = useState('');
  const [onlyOtp, setOnlyOtp] = useState(false);
  const [destType, setDestType] = useState('push');
  const [destTarget, setDestTarget] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get('/messages/rules').then((res) => res.data?.rules || res.data || []),
      api.get('/messages/channels').then((res) => res.data?.channels || res.data || []),
    ]).then(([r, c]) => { setRules(r); setChannels(c); }).catch(() => { setRules([]); }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const channelName = (id) => channels.find((c) => c.id === id)?.name || t('messagesPage.anyChannel');

  const create = useCallback(async () => {
    setSaving(true);
    try {
      await api.post('/messages/rules', {
        channelId: channelId || null,
        match: { sender: sender || undefined, contains: contains || undefined, onlyOtp: onlyOtp || undefined },
        destination: { type: destType, target: destTarget || undefined },
        active: true,
      });
      setShowAdd(false);
      setSender(''); setContains(''); setOnlyOtp(false); setDestTarget('');
      load();
    } catch (e) {
      setToast({ type: 'error', message: e.response?.data?.error || t('messagesPage.saveError') });
    } finally {
      setSaving(false);
    }
  }, [channelId, sender, contains, onlyOtp, destType, destTarget, t, load]);

  const toggleActive = useCallback(async (rule) => {
    try {
      await api.patch(`/messages/rules/${rule.id}`, { active: !rule.active });
      setRules((prev) => prev.map((r) => (r.id === rule.id ? { ...r, active: !r.active } : r)));
    } catch {
      setToast({ type: 'error', message: t('messagesPage.saveError') });
    }
  }, [t]);

  const remove = useCallback(async (id) => {
    if (!window.confirm(t('messagesPage.confirmDeleteRule'))) return;
    try {
      await api.delete(`/messages/rules/${id}`);
      setRules((prev) => prev.filter((r) => r.id !== id));
    } catch {
      setToast({ type: 'error', message: t('messagesPage.deleteError') });
    }
  }, [t]);

  return (
    <div className="sgc-messages__section">
      <div className="sgc-messages__section-header">
        <h2>{t('messagesPage.tabs.rules')}</h2>
        <PillButton icon={PlusIcon} size="sm" onClick={() => setShowAdd(true)}>{t('messagesPage.addRule')}</PillButton>
      </div>

      {loading ? (
        <p className="sgc-messages__loading">{t('common.loading')}</p>
      ) : rules.length === 0 ? (
        <EmptyState icon={SendIcon} title={t('messagesPage.emptyRules')} actionLabel={t('messagesPage.addRule')} onAction={() => setShowAdd(true)} />
      ) : (
        <Card padded={false}>
          <ul className="sgc-messages__rule-list">
            {rules.map((r) => (
              <li key={r.id} className="sgc-messages__rule-row">
                <div className="sgc-messages__rule-info">
                  <p className="sgc-messages__rule-summary">
                    {channelName(r.channelId)}
                    {r.match?.sender ? ` · ${t('messagesPage.field.sender')}: ${r.match.sender}` : ''}
                    {r.match?.contains ? ` · "${r.match.contains}"` : ''}
                    {r.match?.onlyOtp ? ` · ${t('messagesPage.filters.otpOnly')}` : ''}
                  </p>
                  <p className="sgc-messages__help">→ {t(`messagesPage.destination.${r.destination?.type}`)}{r.destination?.target ? `: ${r.destination.target}` : ''}</p>
                </div>
                <Toggle checked={r.active} onChange={() => toggleActive(r)} />
                <button type="button" className="sgc-messages__rule-delete" onClick={() => remove(r.id)} title={t('common.delete')}>
                  <TrashIcon width={16} height={16} aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Modal open={showAdd} onClose={() => setShowAdd(false)} title={t('messagesPage.addRule')}>
        <div className="sgc-messages__rule-form">
          <label className="sgc-messages__help" htmlFor="rule-channel">{t('messagesPage.field.channel')}</label>
          <select id="rule-channel" className="sgc-messages__select" value={channelId} onChange={(e) => setChannelId(e.target.value)}>
            <option value="">{t('messagesPage.anyChannel')}</option>
            {channels.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <Input label={t('messagesPage.field.sender')} value={sender} onChange={(e) => setSender(e.target.value)} />
          <Input label={t('messagesPage.field.contains')} value={contains} onChange={(e) => setContains(e.target.value)} />
          <Toggle label={t('messagesPage.filters.otpOnly')} checked={onlyOtp} onChange={setOnlyOtp} />
          <p className="sgc-messages__help">{t('messagesPage.field.destination')}</p>
          <SegmentedControl
            options={FORWARD_DESTINATIONS.map((d) => ({ value: d, label: t(`messagesPage.destination.${d}`) }))}
            value={destType}
            onChange={setDestType}
          />
          {destType !== 'push' && (
            <Input label={t(`messagesPage.destinationTarget.${destType}`)} value={destTarget} onChange={(e) => setDestTarget(e.target.value)} />
          )}
          <PillButton loading={saving} onClick={create}>{t('common.save')}</PillButton>
        </div>
      </Modal>

      {toast && <div className="sgc-messages__toast"><Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} /></div>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* App Android                                                         */
/* ------------------------------------------------------------------ */

function AndroidTab() {
  const { t } = useTranslation();
  const [key, setKey] = useState('');
  const [deepLink, setDeepLink] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  const generate = useCallback(async () => {
    setGenerating(true);
    setError('');
    try {
      const res = await api.post('/messages/ingest-key');
      const newKey = res.data?.key;
      setKey(newKey);
      // Formato acordado con la app Android: sgc-ingest://<host>?key=<key>
      const link = `sgc-ingest://${window.location.host}?key=${encodeURIComponent(newKey)}`;
      setDeepLink(link);
      const qr = await QRCode.toDataURL(link, { margin: 1, width: 220 }).catch(() => '');
      setQrDataUrl(qr);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setGenerating(false);
    }
  }, []);

  return (
    <div className="sgc-messages__section">
      <Card>
        <div className="sgc-messages__android-intro">
          <IconCircle icon={AndroidIcon} size={48} />
          <div>
            <p className="sgc-messages__channel-name">{t('messagesPage.androidTitle')}</p>
            <p className="sgc-messages__help">{t('messagesPage.androidDescription')}</p>
          </div>
        </div>

        {!key ? (
          <PillButton icon={KeyRoundIcon} loading={generating} onClick={generate}>{t('messagesPage.generateKey')}</PillButton>
        ) : (
          <div className="sgc-messages__android-result">
            {qrDataUrl && <img src={qrDataUrl} alt={t('common.qrCode')} className="sgc-messages__android-qr" />}
            <Input
              readOnly
              value={deepLink}
              rightAction={(
                <button type="button" onClick={() => navigator.clipboard.writeText(deepLink)}>
                  <CopyIcon width={16} height={16} aria-hidden="true" />
                </button>
              )}
            />
            <p className="sgc-messages__help">{t('messagesPage.keyShownOnceHint')}</p>
          </div>
        )}
        {error && <p className="sgc-input__error">{error}</p>}
      </Card>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Página                                                              */
/* ------------------------------------------------------------------ */

const TABS = ['inbox', 'channels', 'rules', 'android'];

function Messages() {
  const { t } = useTranslation();
  const [tab, setTab] = useState('inbox');

  const tabOptions = useMemo(() => TABS.map((tb) => ({ value: tb, label: t(`messagesPage.tabs.${tb}`) })), [t]);

  return (
    <div className="sgc-messages">
      <div className="sgc-messages__header">
        <h1 className="sgc-messages__title">{t('messagesPage.title')}</h1>
      </div>
      <SegmentedControl options={tabOptions} value={tab} onChange={setTab} className="sgc-messages__tabs" />
      {tab === 'inbox' && <InboxTab />}
      {tab === 'channels' && <ChannelsTab />}
      {tab === 'rules' && <RulesTab />}
      {tab === 'android' && <AndroidTab />}
    </div>
  );
}

export default Messages;
